
import { Component, Vue } from 'vue-property-decorator';
import {
  TreeNode,
  NodeType,
  mapProjectToTreeNode,
  mapBudgetLineToTreeNode
} from '@/modules/nomenclature/models/treeNode';
import * as ProjectService from '@/modules/nomenclature/services/projectService';
import ProjectForm from '@/modules/nomenclature/components/ProjectForm.vue';
import DeleteProjectForm from '@/modules/nomenclature/components/DeleteProjectForm.vue';

@Component({
  components: { ProjectForm, DeleteProjectForm }
})
export default class Projects extends Vue {
  protected loading = false;
  protected nodes: TreeNode[] = [];
  protected static id = 1;

  protected created(): void {
    this.reloadProjects();
  }

  protected reloadProjects(): void {
    this.nodes = [];
    this.loading = true;
    ProjectService.fetchAll()
      .then((response) => {
        // Root projects
        for (const project of response.data) {
          const projectNode = mapProjectToTreeNode(project, 0);
          this.nodes.push(projectNode);
        }
        // Virtual node "add project"
        const virtualNode = this.getVirtualNodeAddProject(true);
        virtualNode.depth = 0;
        this.nodes.push(virtualNode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  protected async fetchProjectChildren(item: TreeNode): Promise<void> {
    return ProjectService.fetchChildren(item)
      .then((response) => {
        item.children = [];
        const depth = item.depth !== undefined ? item.depth + 1 : -1;
        // Sub-projects
        for (const child of response.data) {
          const projectNode = mapProjectToTreeNode(child, depth);
          item.children.push(projectNode);
        }
        // Budget lines
        if (item.hiddenChildren) {
          item.children.push(...item.hiddenChildren);
        }
        // Virtual node "add project or budget lines"
        const virtualNode = this.getVirtualNodeAddProject();
        virtualNode.depth = depth;
        virtualNode.parentId = item.id;
        item.children.push(virtualNode);
      })
      .catch(() => {
        return;
      });
  }

  protected createNode(entity: TreeNode): Promise<void> {
    return new Promise((resolve, reject) => {
      if (entity.nodeType === NodeType.PROJECT) {
        ProjectService.create(entity)
          .then((projectResponse) => {
            const projectNode = mapProjectToTreeNode(projectResponse.data);
            this.insertNode(projectNode);
            resolve();
          })
          .catch((e) => (reject(e), true));
      } else if (entity.nodeType === NodeType.BUDGET_LINE && entity.parentId) {
        entity.icon = 'mdi-cash';
        ProjectService.createBudgetLine(entity.parentId, entity)
          .then((budgetLineResponse) => {
            const projectNode = mapBudgetLineToTreeNode(budgetLineResponse.data, entity.parentId as string);
            this.insertNode(projectNode);
            resolve();
          })
          .catch((e) => (reject(e), true));
      } else {
        reject();
      }
    });
  }

  protected updateNode(entity: TreeNode): Promise<void> {
    return new Promise((resolve, reject) => {
      if (entity.nodeType === NodeType.PROJECT) {
        ProjectService.update(entity)
          .then((updateResponse) => {
            const updatedNode = mapProjectToTreeNode(updateResponse.data);
            const old = this.findNodeInTree(entity.id as string, this.nodes);
            updatedNode.depth = old?.depth;
            updatedNode.children = old?.children;
            updatedNode.hiddenChildren = old?.hiddenChildren;
            Object.assign(old, updatedNode);
            resolve();
          })
          .catch(() => reject());
      } else if (entity.nodeType === NodeType.BUDGET_LINE && entity.parentId) {
        ProjectService.updateBudgetLine(entity)
          .then((updateResponse) => {
            const updatedNode = mapBudgetLineToTreeNode(updateResponse.data, entity.parentId as string);
            const old = this.findNodeInTree(entity.id as string, this.nodes);
            updatedNode.depth = old?.depth;
            updatedNode.children = old?.children;
            updatedNode.hiddenChildren = old?.hiddenChildren;
            Object.assign(old, updatedNode);
            resolve();
          })
          .catch(() => reject());
      } else {
        reject();
      }
    });
  }

  protected deleteNode(node: TreeNode, onDeleteCascadeRcv: boolean): Promise<void> {
    const param = { onDeleteCascade: onDeleteCascadeRcv };
    return new Promise((resolve, reject) => {
      if (node.nodeType === NodeType.PROJECT && node.id) {
        ProjectService.destroy(node.id, param)
          .then(() => {
            this.removeNode(node);
            resolve();
          })
          .catch((e) => reject(e));
      } else if (node.id && node.parentId) {
        ProjectService.destroyBudgetLine(node.id, param)
          .then(() => {
            this.removeNode(node);
            resolve();
          })
          .catch((e) => reject(e));
      } else {
        reject();
      }
    });
  }

  protected insertNode(nodeParam: TreeNode, tree: TreeNode[] | undefined = undefined): void {
    if (tree === undefined) {
      // If this is the first step, start with root nodes
      tree = this.nodes;
    }
    if (nodeParam.parentId === null) {
      // if the user wants to insert a root node, immediatly returns
      nodeParam.depth = 0;
      tree.push(nodeParam);
      tree.sort(this.sortChildrenNodes);
    } else {
      // else find parent node in graph
      const parentNode = this.findNodeInTree(nodeParam.parentId as string, tree);
      if (parentNode) {
        // parent is found, insert it into children array
        nodeParam.depth = parentNode.depth !== undefined ? parentNode.depth + 1 : -1;
        parentNode.children?.push(nodeParam);
        parentNode.children?.sort(this.sortChildrenNodes);
      }
    }
  }

  protected removeNode(node: TreeNode): void {
    if (node.parentId) {
      const parent = this.findNodeInTree(node.parentId, this.nodes);
      if (parent) {
        parent.children = parent?.children?.filter((item) => item.id !== node.id);
      }
    } else {
      this.nodes = this.nodes.filter((item) => item.id !== node.id);
    }
  }

  protected findNodeInTree(nodeId: string, tree: TreeNode[]): TreeNode | null {
    for (const node of tree) {
      if (node.id === nodeId) {
        // node is found, return it
        return node;
      }
      if (node.children) {
        const foundNode = this.findNodeInTree(nodeId, node.children);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null;
  }

  protected sortChildrenNodes(first: TreeNode, second: TreeNode): number {
    const order: Record<string, number> = {};
    order[NodeType.PROJECT] = 1;
    order[NodeType.BUDGET_LINE] = 2;
    order[NodeType.VIRTUAL] = 3;

    if (order[first.nodeType] !== order[second.nodeType]) {
      return order[first.nodeType] - order[second.nodeType];
    } else {
      return first.name.localeCompare(second.name);
    }
  }

  protected getVirtualNodeAddProject(isRoot = false): TreeNode {
    return {
      id: (Projects.id++).toString(),
      icon: this.$i18n.t('common.actions.add.icon').toString(),
      name: isRoot
        ? this.$i18n.t('nomenclature.Projects.newProjectLabel').toString()
        : this.$i18n.t('nomenclature.Projects.newProjectBudgetLineLabel').toString(),
      parentId: null,
      nodeType: NodeType.VIRTUAL
    };
  }

  protected getDepthColorClass(depth: number): string {
    switch (depth) {
      case 0:
        return 'depth-zero';
      case 1:
        return 'depth-one';
      case 2:
        return 'depth-two';
      case 3:
        return 'depth-three';
      case 4:
        return 'depth-four';
      case 5:
        return 'depth-five';
      default:
        return 'depth-unknown';
    }
  }

  protected get NodeType(): Record<string, string> {
    return NodeType;
  }
}
